import { useRef, useCallback } from 'react';

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  businessProfileHash: string;
}

interface ApiCache {
  [key: string]: CacheEntry<any>;
}

const CACHE_DURATION = 5 * 60 * 1000; // 5 minuti

export const useApiCache = () => {
  const cacheRef = useRef<ApiCache>({});
  const fetchingRef = useRef<Set<string>>(new Set());

  const getCacheKey = useCallback((apiName: string, businessProfile: any, params?: any) => {
    const businessProfileHash = JSON.stringify(businessProfile);
    const paramsHash = params ? JSON.stringify(params) : '';
    return `${apiName}_${businessProfileHash}_${paramsHash}`;
  }, []);

  const isCached = useCallback((cacheKey: string): boolean => {
    const entry = cacheRef.current[cacheKey];
    if (!entry) return false;
    
    const now = Date.now();
    return (now - entry.timestamp) < CACHE_DURATION;
  }, []);

  const getCachedData = useCallback((cacheKey: string) => {
    const entry = cacheRef.current[cacheKey];
    return entry ? entry.data : null;
  }, []);

  const setCachedData = useCallback((cacheKey: string, data: any, businessProfile: any) => {
    cacheRef.current[cacheKey] = {
      data,
      timestamp: Date.now(),
      businessProfileHash: JSON.stringify(businessProfile)
    };
  }, []);

  const isFetching = useCallback((cacheKey: string): boolean => {
    return fetchingRef.current.has(cacheKey);
  }, []);

  const setFetching = useCallback((cacheKey: string, fetching: boolean) => {
    if (fetching) {
      fetchingRef.current.add(cacheKey);
    } else {
      fetchingRef.current.delete(cacheKey);
    }
  }, []);

  const clearCache = useCallback(() => {
    cacheRef.current = {};
    fetchingRef.current.clear();
  }, []);

  return {
    getCacheKey,
    isCached,
    getCachedData,
    setCachedData,
    isFetching,
    setFetching,
    clearCache
  };
};